import { motion } from 'framer-motion'
import {
  ResourceLayout, ResourceHero, ResourceContactCTA, CodeBlock,
  DownloadButton, DownloadRow, StatusBlock, SignatureFooter,
  H2, ACCENTS, MONO, fade,
} from './_shared'

const A = ACCENTS.red

const SIGNS = [
  {
    title: 'Queda brusca de tráfego orgânico sem motivo aparente',
    desc: 'Sem mudança de conteúdo, sem update, sem migração. O Google rebaixou o domínio porque detectou conteúdo de spam.',
  },
  {
    title: 'Páginas indexadas que você nunca criou',
    desc: 'No Google, busca site:seudominio.com cassino OR aposta OR bet OR slot. Se aparecer resultado, você foi injetado.',
  },
  {
    title: 'CPL subindo sem mudar campanha',
    desc: 'Quality Score cai quando o domínio está sujo. O leilão de Ads fica mais caro mesmo com o mesmo criativo e público.',
  },
  {
    title: 'Posts com autor desconhecido no banco',
    desc: 'O atacante cria (ou usa) um post_author pra publicar em massa. Um único author_id com milhares de posts é o sinal mais claro.',
  },
]

const DETECT_SQL = `-- Conta posts por autor: o atacante costuma usar 1 author_id em massa
SELECT post_author, COUNT(*) AS total
FROM wp_posts
WHERE post_type = 'post' AND post_status = 'publish'
GROUP BY post_author
ORDER BY total DESC;

-- Procura títulos/conteúdo com termos de spam
SELECT ID, post_title, post_author, post_date
FROM wp_posts
WHERE post_status = 'publish'
  AND (post_title REGEXP 'casino|aposta|bet|slot|bonus'
   OR  post_content REGEXP 'casino|aposta|bet|slot|bonus')
ORDER BY post_date DESC
LIMIT 50;

-- Backdoor em MySQL: triggers escondidos que recriam admin
SHOW TRIGGERS;
-- se aparecer um after_insert em wp_comments/wp_users que voce nao criou, e backdoor`

const DETECT_SH = `# Webshells e PHP malicioso nos diretorios do WordPress
grep -rEl "eval\\(base64_decode|gzinflate\\(base64|str_rot13\\(" wp-content/ 2>/dev/null
grep -rl "FilesMan\\|WSO \\|c99sh\\|r57shell" wp-content/ 2>/dev/null

# PHP executavel onde NUNCA deveria existir (uploads)
find wp-content/uploads -name "*.php" -type f

# Arquivos modificados nas ultimas 48h (pode revelar injecao recente)
find wp-content -name "*.php" -mtime -2 -type f`

const REMEDIATION = [
  {
    n: '01',
    t: 'Backup ANTES de deletar',
    d: 'Dump do banco e dos arquivos. Você vai apagar em lote — se errar o author_id, precisa reverter.',
  },
  {
    n: '02',
    t: 'Deleta em lote por author_id',
    d: 'Identificado o autor do spam, apaga os posts dele em uma query (DELETE FROM wp_posts WHERE post_author = X). Milhares somem em segundos.',
  },
  {
    n: '03',
    t: '410 Gone, não 404',
    d: 'Configura as URLs removidas pra retornar 410 (Gone). Sinaliza pro Google remoção permanente — desindexação mais rápida que 404.',
  },
  {
    n: '04',
    t: 'Purga cache + CDN',
    d: 'O conteúdo injetado pode estar em cache de página e na borda do CDN. Limpa os dois ou o spam continua sendo servido.',
  },
  {
    n: '05',
    t: 'Reenvia o sitemap no GSC',
    d: 'Força o Google a recrawlar. Acompanha a desindexação em Cobertura/Páginas nas semanas seguintes.',
  },
  {
    n: '06',
    t: 'Acha o vetor e fecha',
    d: 'FTP comprometido, plugin desatualizado, senha vazada. Rotaciona TODAS as credenciais (FTP, DB, admin) e remove webshells. Sem isso, eles voltam.',
  },
]

export default function SeoPoisoningDetect() {
  return (
    <ResourceLayout>
      <ResourceHero
        tag="recurso · cyber · seo poisoning"
        title="Como identificar SEO poisoning no seu WordPress"
        desc="SEO poisoning é a injeção de milhares de posts de spam (cassino, aposta) em sites legítimos pra sequestrar a autoridade de domínio. Aqui estão os sinais de alerta, os comandos de detecção (SQL + bash) e o passo a passo de limpeza."
        accent={A}
        source="seo-poisoning-detect"
        word="PARASITA"
      />

      <StatusBlock accent={A} rows={[
        { k: 'tipo', v: 'cyber · detecção SEO poisoning' },
        { k: 'formato', v: 'sinais + queries SQL + greps bash + remediação' },
        { k: 'esforço', v: '10min pra rodar a varredura inicial' },
        { k: 'valor', v: 'descobre injeção antes do Google te penalizar' },
      ]} />

      <motion.section {...fade(0.06)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>sinais de alerta</H2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {SIGNS.map((s, i) => (
            <div key={i} style={{
              padding: '1rem 1.2rem',
              background: 'linear-gradient(180deg, #111, #0d0d0d)',
              border: '1px solid #1e1e1e',
              borderLeft: `3px solid ${A.solid}`,
              borderRadius: 8,
            }}>
              <p style={{ fontFamily: MONO, fontSize: '0.82rem', color: A.text, fontWeight: 600, marginBottom: '0.4rem' }}>
                {i + 1}. {s.title}
              </p>
              <p style={{ fontSize: '0.82rem', color: '#aaa', lineHeight: 1.6 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </motion.section>

      <motion.section {...fade(0.1)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>detecção no banco (SQL)</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>
          Roda via phpMyAdmin, Adminer ou linha de comando. O scanner de arquivo PHP não vê injeção que vive só no banco — por isso a query é essencial.
        </p>
        <DownloadRow>
          <DownloadButton content={DETECT_SQL} filename="detect-seo-poisoning.sql" accent={A} source="seo-poisoning-sql" mime="application/sql" />
        </DownloadRow>
        <CodeBlock lang="sql" accent={A} source="seo-poisoning-detect">{DETECT_SQL}</CodeBlock>
      </motion.section>

      <motion.section {...fade(0.13)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>detecção nos arquivos (bash)</H2>
        <p style={{ color: '#aaa', fontSize: '0.85rem', lineHeight: 1.65, marginBottom: '1rem' }}>
          Roda na raiz do WordPress via SSH. Caça webshells, PHP em uploads e arquivos alterados recentemente.
        </p>
        <DownloadRow>
          <DownloadButton content={DETECT_SH} filename="detect-webshells.sh" accent={A} source="seo-poisoning-sh" mime="application/x-sh" />
        </DownloadRow>
        <CodeBlock lang="bash" accent={A} source="seo-poisoning-detect">{DETECT_SH}</CodeBlock>
      </motion.section>

      <motion.section {...fade(0.16)} style={{ marginBottom: '2.5rem' }}>
        <H2 accent={A}>achou? o que fazer (6 passos)</H2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          {REMEDIATION.map((r, i) => (
            <div key={i} style={{
              display: 'flex', gap: '1rem', alignItems: 'flex-start',
              padding: '1rem 1.2rem',
              background: 'linear-gradient(180deg, #111, #0d0d0d)',
              border: '1px solid #1e1e1e',
              borderRadius: 8,
            }}>
              <span style={{ fontFamily: MONO, fontSize: '1.1rem', color: A.text, fontWeight: 800, flexShrink: 0 }}>{r.n}</span>
              <div>
                <p style={{ fontSize: '0.88rem', color: '#fff', fontWeight: 600, marginBottom: '0.3rem' }}>{r.t}</p>
                <p style={{ fontSize: '0.82rem', color: '#aaa', lineHeight: 1.6 }}>{r.d}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.section>

      <ResourceContactCTA accent={A} source="seo-poisoning-detect" />
      <SignatureFooter accent={A} />
    </ResourceLayout>
  )
}
